import React, { useCallback, useEffect, useMemo } from 'react';
import { Modal, notification } from 'antd';
import {
  setGlobalFeedbackHandlers,
  clearGlobalFeedbackHandlers,
} from './globalFeedbackBridge';

const DEDUP_MS = 600;
const lastShownKeyAt = new Map();

function notificationDedupeKey(err) {
  const u = err?.url || '';
  const m = err?.message || '';
  return `${u}|${m}`;
}

const GlobalFeedbackContext = React.createContext(null);

export function useGlobalFeedback() {
  return React.useContext(GlobalFeedbackContext);
}

/**
 * Hosts imperative feedback hooks for non-React callers (e.g. axios).
 * Registers antd notification for REST errors and owns blocking-modal teardown.
 */
export function GlobalFeedbackProvider({ children }) {
  const showRestError = useCallback((apiClientError) => {
    const key = notificationDedupeKey(apiClientError);
    const now = Date.now();
    const prev = lastShownKeyAt.get(key) || 0;
    if (now - prev < DEDUP_MS) {
      return;
    }
    lastShownKeyAt.set(key, now);

    notification.error({
      key,
      message: 'Request failed',
      description: apiClientError?.message || 'Something went wrong.',
      duration: 4.5,
    });
  }, []);

  const destroyBlockingUI = useCallback(() => {
    Modal.destroyAll();
  }, []);

  useEffect(() => {
    setGlobalFeedbackHandlers({ showRestError, destroyBlockingUI });
    return () => {
      clearGlobalFeedbackHandlers();
    };
  }, [showRestError, destroyBlockingUI]);

  const value = useMemo(
    () => ({
      showRestError,
      destroyBlockingUI,
    }),
    [showRestError, destroyBlockingUI],
  );

  return (
    <GlobalFeedbackContext.Provider value={value}>
      {children}
    </GlobalFeedbackContext.Provider>
  );
}
